import type { Feature, FeatureCollection, Polygon, MultiPolygon, Point, Position } from "geojson";
import type { Job } from "../../../../../../models";
import type {
	OverlapIssue,
	DuplicateIssue,
	InternalDuplicateIssue,
	DuplicateGroup,
	UnmatchedIssue,
	MissingFeaturesIssue,
	NearbyJobInfo,
	OverlappingZonesIssue,
	OverlapGroup,
} from "../WizardContainer";
import type { BBox } from "../../../../../../utilities/types";

// Import turf functions - available via @rogoag/shared-ts transitive dependency
import * as turf from "@turf/turf";
// Import geojson-rbush for spatial indexing (available as transitive dependency)
import geojsonRbush from "geojson-rbush";

// ============================================================================
// Performance Optimizations
// ============================================================================

/**
 * Generates a deterministic issue ID from a prefix and job ID(s).
 * Stable across re-validations so that skippedIssueIds survives SET_JOBS.
 */
function issueId(prefix: string, jobIds: string | string[]): string {
	const suffix = Array.isArray(jobIds) ? [...jobIds].sort().join("-") : jobIds;
	return `${prefix}-${suffix}`;
}

/**
 * Cache for computed boundary centroids (job.id -> centroid)
 * Avoids recomputing centroids for the same boundary multiple times
 */
/** Minimum overlap area (in square meters) to report as an issue */
const MIN_OVERLAP_AREA_SQ_METERS = 2_000; // ~0.5 acres

const boundaryCentroidCache = new WeakMap<Job, Feature<Point> | null>();

/**
 * Gets the cached centroid for a job's boundary, or computes and caches it
 */
function getCachedBoundaryCentroid(job: Job): Feature<Point> | null {
	if (boundaryCentroidCache.has(job)) {
		return boundaryCentroidCache.get(job) ?? null;
	}
	const boundary = getBoundaryFeature(job);
	if (!boundary) {
		boundaryCentroidCache.set(job, null);
		return null;
	}
	try {
		const centroid = turf.centroid(boundary);
		boundaryCentroidCache.set(job, centroid);
		return centroid;
	} catch {
		boundaryCentroidCache.set(job, null);
		return null;
	}
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Gets the boundary feature from a job, handling both single polygons and multi-polygons
 */
function getBoundaryFeature(job: Job): Feature<Polygon | MultiPolygon> | null {
	const boundary = job.files?.boundaryGeoJSON ?? job.fileOptions?.boundaryGeoJSON;
	if (!boundary || !boundary.features || boundary.features.length === 0) {
		return null;
	}
	// Return the first feature (usually there's only one boundary per job)
	return boundary.features[0] as Feature<Polygon | MultiPolygon>;
}

/**
 * Checks if a job has a boundary
 */
function hasBoundary(job: Job): boolean {
	return getBoundaryFeature(job) !== null;
}

/**
 * Checks if a job has points or zones (features that need to match to a boundary)
 */
function hasFeatures(job: Job): boolean {
	const points = job.files?.pointsGeoJSON ?? job.fileOptions?.pointsGeoJSON;
	const zones = job.files?.zonesGeoJSON ?? job.fileOptions?.zonesGeoJSON;
	return (points?.features?.length ?? 0) > 0 || (zones?.features?.length ?? 0) > 0;
}

/**
 * Gets all point features from a job (from pointsGeoJSON)
 */
function getPointFeatures(job: Job): Feature<Point>[] {
	const points = job.files?.pointsGeoJSON ?? job.fileOptions?.pointsGeoJSON;
	if (!points?.features) return [];
	return points.features.filter((f): f is Feature<Point> => f.geometry?.type === "Point");
}

/**
 * Gets all zone features from a job (from zonesGeoJSON)
 */
function getZoneFeatures(job: Job): Feature<Polygon | MultiPolygon>[] {
	const zones = job.files?.zonesGeoJSON ?? job.fileOptions?.zonesGeoJSON;
	if (!zones?.features) return [];
	return zones.features.filter(
		(f): f is Feature<Polygon | MultiPolygon> => f.geometry?.type === "Polygon" || f.geometry?.type === "MultiPolygon",
	);
}

/**
 * Rounds coordinates to 6 decimal places for comparison
 * 6 decimal places = ~0.11 meters precision
 * Uses integer math for performance (multiply to int, round, keep as int for key generation)
 */
const COORD_PRECISION = 1000000;

/** Points within this distance are considered duplicates */
const DUPLICATE_POINT_PROXIMITY_FEET = 30;

/**
 * Creates a coordinate key for hashing/comparison
 * Uses integer representation to avoid floating point string conversion overhead
 */
function createCoordinateKey(coords: Position): string {
	// Use integer keys for faster comparison (avoid floating point formatting)
	const x = Math.round(coords[0] * COORD_PRECISION);
	const y = Math.round(coords[1] * COORD_PRECISION);
	return `${x},${y}`;
}

/**
 * Gets the centroid of a job's features (points or zones)
 */
function getFeaturesCentroid(job: Job): Feature<Point> | null {
	const points = getPointFeatures(job);
	const zones = getZoneFeatures(job);

	// Use first point if available
	if (points.length > 0) {
		return points[0];
	}

	// Otherwise use centroid of first zone
	if (zones.length > 0) {
		try {
			return turf.centroid(zones[0]);
		} catch {
			return null;
		}
	}

	return null;
}

/**
 * Gets FilePath property from features
 */
function getFilePath(features: Feature[]): string {
	for (const feature of features) {
		if (feature.properties?.FilePath) {
			return feature.properties.FilePath;
		}
	}
	return "";
}

/**
 * Builds a map of jobId to file paths from entries with jobId and filePath
 * Uses Set for O(1) duplicate checking instead of includes() O(n)
 */
function buildJobFilePaths(entries: Array<{ jobId: string; filePath: string }>): Record<string, string[]> {
	const result: Record<string, string[]> = {};
	const seenPaths: Record<string, Set<string>> = {};

	for (const entry of entries) {
		if (!result[entry.jobId]) {
			result[entry.jobId] = [];
			seenPaths[entry.jobId] = new Set();
		}
		if (entry.filePath && !seenPaths[entry.jobId].has(entry.filePath)) {
			seenPaths[entry.jobId].add(entry.filePath);
			result[entry.jobId].push(entry.filePath);
		}
	}
	return result;
}

/**
 * Calculates the bounding box of a feature
 */
function getBbox(feature: Feature<Polygon | MultiPolygon>): BBox {
	return turf.bbox(feature) as BBox;
}

/**
 * Checks if two bounding boxes intersect (fast pre-filter)
 */
function bboxesIntersect(bbox1: BBox, bbox2: BBox): boolean {
	// bbox format: [minX, minY, maxX, maxY]
	return !(bbox1[2] < bbox2[0] || bbox1[0] > bbox2[2] || bbox1[3] < bbox2[1] || bbox1[1] > bbox2[3]);
}

/**
 * Creates a geometry key for boundary comparison.
 * Uses coordinate hashing to identify identical geometries.
 * Optimized with pre-allocated arrays and integer-based coordinate keys.
 */
function createBoundaryKey(feature: Feature<Polygon | MultiPolygon>): string {
	const geom = feature.geometry;
	if (geom.type === "Polygon") {
		// Use outer ring coordinates with pre-allocated array
		const ring = geom.coordinates[0];
		const parts = new Array<string>(ring.length);
		for (let i = 0; i < ring.length; i++) {
			const c = ring[i];
			parts[i] = `${Math.round(c[0] * COORD_PRECISION)},${Math.round(c[1] * COORD_PRECISION)}`;
		}
		return parts.join("|");
	} else if (geom.type === "MultiPolygon") {
		// Use all outer rings with pre-allocated arrays
		const polyParts = new Array<string>(geom.coordinates.length);
		for (let p = 0; p < geom.coordinates.length; p++) {
			const ring = geom.coordinates[p][0];
			const parts = new Array<string>(ring.length);
			for (let i = 0; i < ring.length; i++) {
				const c = ring[i];
				parts[i] = `${Math.round(c[0] * COORD_PRECISION)},${Math.round(c[1] * COORD_PRECISION)}`;
			}
			polyParts[p] = parts.join("|");
		}
		return polyParts.join("||");
	}
	return "";
}

// ============================================================================
// Main Detection Functions
// ============================================================================

/**
 * Detects jobs with overlapping boundaries.
 * Returns a list of overlap issues, each containing the jobs whose boundaries intersect.
 *
 * Performance: Uses R-tree spatial indexing for large datasets (50+ jobs)
 * and lazy boundary key computation to avoid overhead for non-overlapping cases.
 */
export function detectOverlappingBoundaries(jobs: Job[]): OverlapIssue[] {
	const issues: OverlapIssue[] = [];

	// Filter to jobs with boundaries and prepare data
	type JobBoundaryItem = {
		job: Job;
		boundary: Feature<Polygon | MultiPolygon>;
		bbox: BBox;
		_boundaryKey?: string; // Lazily computed
	};

	const jobsWithBoundaries: JobBoundaryItem[] = [];
	const jobIdToItem = new Map<string, JobBoundaryItem>();

	for (const job of jobs) {
		const boundary = getBoundaryFeature(job);
		if (!boundary) continue;

		const item: JobBoundaryItem = {
			job,
			boundary,
			bbox: getBbox(boundary),
			// boundaryKey computed lazily when needed
		};
		jobsWithBoundaries.push(item);
		jobIdToItem.set(job.id, item);
	}

	// Need at least 2 jobs to have overlaps
	if (jobsWithBoundaries.length < 2) {
		return [];
	}

	// Helper to get or compute boundary key lazily
	const getBoundaryKey = (item: JobBoundaryItem): string => {
		if (item._boundaryKey === undefined) {
			item._boundaryKey = createBoundaryKey(item.boundary);
		}
		return item._boundaryKey;
	};

	// Build R-tree spatial index for fast bbox queries
	// For small datasets (< 50 jobs), the O(n²) approach is faster due to R-tree overhead
	const useRtree = jobsWithBoundaries.length >= 50;

	// Track which pairs we've already checked
	const checkedPairs = new Set<string>();

	if (useRtree) {
		// Build R-tree index
		const tree = geojsonRbush<Polygon | MultiPolygon>();
		const indexedFeatures: Feature<Polygon | MultiPolygon>[] = [];

		for (const item of jobsWithBoundaries) {
			// Add job ID to feature properties for lookup
			const featureWithId = {
				...item.boundary,
				properties: { ...item.boundary.properties, __jobId: item.job.id },
			};
			indexedFeatures.push(featureWithId);
		}
		tree.load(indexedFeatures);

		// For each job, query the R-tree for potential overlaps
		for (const item1 of jobsWithBoundaries) {
			// Query R-tree for features that intersect this bounding box
			const candidates = tree.search(item1.bbox);

			for (const candidateFeature of candidates.features) {
				const item2JobId = candidateFeature.properties?.__jobId as string;
				if (!item2JobId || item2JobId === item1.job.id) continue;

				const item2 = jobIdToItem.get(item2JobId);
				if (!item2) continue;

				// Create pair key to avoid duplicates
				const pairKey =
					item1.job.id < item2.job.id ? `${item1.job.id}-${item2.job.id}` : `${item2.job.id}-${item1.job.id}`;
				if (checkedPairs.has(pairKey)) continue;
				checkedPairs.add(pairKey);

				// Check for exact duplicates (handled by duplicate detection) - computed lazily
				if (getBoundaryKey(item1) === getBoundaryKey(item2)) continue;

				try {
					const intersection = turf.intersect(turf.featureCollection([item1.boundary, item2.boundary]));
					if (intersection) {
						const intersectionArea = turf.area(intersection);
						if (intersectionArea > MIN_OVERLAP_AREA_SQ_METERS) {
							const jobFilePaths: Record<string, string[]> = {};
							const filePath1 = item1.boundary.properties?.FilePath;
							const filePath2 = item2.boundary.properties?.FilePath;
							if (filePath1) jobFilePaths[item1.job.id] = [filePath1];
							if (filePath2) jobFilePaths[item2.job.id] = [filePath2];

							issues.push({
								id: issueId("overlap", [item1.job.id, item2.job.id]),
								jobIds: [item1.job.id, item2.job.id],
								jobs: [item1.job, item2.job],
								intersectionArea,
								intersectionGeometry: intersection as Feature<Polygon | MultiPolygon>,
								jobFilePaths,
							});
						}
					}
				} catch {
					// Skip invalid geometries
				}
			}
		}
	} else {
		// Small dataset: use simple O(n²) with bbox pre-filter
		for (let i = 0; i < jobsWithBoundaries.length; i++) {
			for (let j = i + 1; j < jobsWithBoundaries.length; j++) {
				const item1 = jobsWithBoundaries[i];
				const item2 = jobsWithBoundaries[j];

				// Fast pre-filter: skip if bounding boxes don't intersect
				if (!bboxesIntersect(item1.bbox, item2.bbox)) continue;

				// Create pair key to avoid duplicates
				const pairKey = `${item1.job.id}-${item2.job.id}`;
				if (checkedPairs.has(pairKey)) continue;
				checkedPairs.add(pairKey);

				// Check for exact duplicates (handled by duplicate detection) - computed lazily
				if (getBoundaryKey(item1) === getBoundaryKey(item2)) continue;

				try {
					const intersection = turf.intersect(turf.featureCollection([item1.boundary, item2.boundary]));
					if (intersection) {
						const intersectionArea = turf.area(intersection);
						if (intersectionArea > MIN_OVERLAP_AREA_SQ_METERS) {
							const jobFilePaths: Record<string, string[]> = {};
							const filePath1 = item1.boundary.properties?.FilePath;
							const filePath2 = item2.boundary.properties?.FilePath;
							if (filePath1) jobFilePaths[item1.job.id] = [filePath1];
							if (filePath2) jobFilePaths[item2.job.id] = [filePath2];

							issues.push({
								id: issueId("overlap", [item1.job.id, item2.job.id]),
								jobIds: [item1.job.id, item2.job.id],
								jobs: [item1.job, item2.job],
								intersectionArea,
								intersectionGeometry: intersection as Feature<Polygon | MultiPolygon>,
								jobFilePaths,
							});
						}
					}
				} catch {
					// Skip invalid geometries
				}
			}
		}
	}

	issues.sort((a, b) => b.intersectionArea - a.intersectionArea);

	return issues;
}

/**
 * Detects duplicate BOUNDARIES across jobs.
 * Returns issues when 2+ jobs have identical field boundaries.
 *
 * NOTE: Does NOT detect duplicate points or zones (that's handled by detectInternalDuplicates)
 */
export function detectDuplicateBoundaries(jobs: Job[]): DuplicateIssue[] {
	const issues: DuplicateIssue[] = [];

	// Maps boundary key to list of { jobId, feature, filePath }
	const boundaryMap = new Map<
		string,
		Array<{ jobId: string; feature: Feature<Polygon | MultiPolygon>; filePath: string }>
	>();

	// Collect boundaries from all jobs
	for (const job of jobs) {
		const boundary = getBoundaryFeature(job);
		if (boundary) {
			const key = createBoundaryKey(boundary);
			const existing = boundaryMap.get(key) || [];
			existing.push({
				jobId: job.id,
				feature: boundary,
				filePath: boundary.properties?.FilePath || "",
			});
			boundaryMap.set(key, existing);
		}
	}

	// Find duplicates (2+ jobs with same boundary)
	for (const [, entries] of boundaryMap) {
		const uniqueJobIds = [...new Set(entries.map((e) => e.jobId))];
		if (uniqueJobIds.length > 1) {
			// Multiple jobs have the exact same boundary - this is a duplicate
			const filePaths = [...new Set(entries.map((e) => e.filePath).filter(Boolean))];
			const jobFilePaths = buildJobFilePaths(entries);
			const affectedJobs = jobs.filter((j) => uniqueJobIds.includes(j.id));

			issues.push({
				id: issueId("dup-bnd", uniqueJobIds),
				jobIds: uniqueJobIds,
				jobs: affectedJobs,
				featureType: "boundary",
				duplicateCount: entries.length,
				filePaths,
				jobFilePaths,
				duplicateFeatures: entries.map((e) => e.feature),
			});
		}
	}

	return issues;
}

/**
 * Creates coordinate key for zone (uses centroid for comparison)
 */
function createZoneCoordinateKey(geometry: Polygon | MultiPolygon): string {
	try {
		const centroid = turf.centroid(turf.feature(geometry));
		return createCoordinateKey(centroid.geometry.coordinates);
	} catch {
		// Fallback: use first coordinate of first ring
		const coords = geometry.type === "Polygon" ? geometry.coordinates[0][0] : geometry.coordinates[0][0][0];
		return createCoordinateKey(coords);
	}
}

/**
 * Groups features by their coordinate key
 */
function groupFeaturesByCoordinate(
	features: Array<{ feature: Feature<Point | Polygon | MultiPolygon>; index: number }>,
	type: "point" | "zone",
): DuplicateGroup[] {
	const groups = new Map<string, { features: Feature<Point | Polygon | MultiPolygon>[]; indices: number[] }>();

	for (const { feature, index } of features) {
		let key: string;
		if (type === "point") {
			key = createCoordinateKey((feature.geometry as Point).coordinates);
		} else {
			key = createZoneCoordinateKey(feature.geometry as Polygon | MultiPolygon);
		}

		const existing = groups.get(key) || { features: [], indices: [] };
		existing.features.push(feature);
		existing.indices.push(index);
		groups.set(key, existing);
	}

	return Array.from(groups.entries()).map(([key, { features: groupFeatures, indices }]) => ({
		id: `group-${key.substring(0, 12)}`,
		type,
		features: groupFeatures,
		coordinateKey: key,
		featureIndices: indices,
	}));
}

/**
 * Groups point features by proximity using union-find.
 * Any points within DUPLICATE_POINT_PROXIMITY_FEET of each other are grouped together transitively.
 * Only returns groups with 2+ features.
 */
function groupPointsByProximity(
	features: Array<{ feature: Feature<Point | Polygon | MultiPolygon>; index: number }>,
): DuplicateGroup[] {
	const n = features.length;
	if (n < 2) return [];

	// Union-Find with path compression and union by rank
	const parent = new Array<number>(n);
	const rank = new Array<number>(n);
	for (let i = 0; i < n; i++) {
		parent[i] = i;
		rank[i] = 0;
	}

	function find(x: number): number {
		while (parent[x] !== x) {
			parent[x] = parent[parent[x]]; // path compression
			x = parent[x];
		}
		return x;
	}

	function union(a: number, b: number): void {
		const ra = find(a);
		const rb = find(b);
		if (ra === rb) return;
		if (rank[ra] < rank[rb]) {
			parent[ra] = rb;
		} else if (rank[ra] > rank[rb]) {
			parent[rb] = ra;
		} else {
			parent[rb] = ra;
			rank[ra]++;
		}
	}

	// O(n²) pairwise distance check — fine for typical job sizes (5-100 points)
	for (let i = 0; i < n; i++) {
		for (let j = i + 1; j < n; j++) {
			const pt1 = features[i].feature as Feature<Point>;
			const pt2 = features[j].feature as Feature<Point>;
			const dist = turf.distance(pt1, pt2, { units: "feet" });
			if (dist <= DUPLICATE_POINT_PROXIMITY_FEET) {
				union(i, j);
			}
		}
	}

	// Collect groups by root
	const groups = new Map<number, { features: Feature<Point | Polygon | MultiPolygon>[]; indices: number[] }>();
	for (let i = 0; i < n; i++) {
		const root = find(i);
		const existing = groups.get(root) || { features: [], indices: [] };
		existing.features.push(features[i].feature);
		existing.indices.push(features[i].index);
		groups.set(root, existing);
	}

	// Only return groups with 2+ features (actual duplicates)
	return Array.from(groups.values())
		.filter((g) => g.features.length > 1)
		.map(({ features: groupFeatures, indices }) => {
			// Use first point's coordinate as the key for identification
			const firstCoords = (groupFeatures[0].geometry as Point).coordinates;
			const key = createCoordinateKey(firstCoords);
			return {
				id: `group-${key.substring(0, 12)}`,
				type: "point" as const,
				features: groupFeatures,
				coordinateKey: key,
				featureIndices: indices,
			};
		});
}

/**
 * Detects duplicate points/zones WITHIN individual jobs.
 * Returns issues for jobs that have 2+ features at the same location.
 *
 * Algorithm:
 * 1. For each job, extract all points and zones
 * 2. Generate coordinate key for each feature (6 decimal precision ~0.1m)
 * 3. Group features by coordinate key
 * 4. Create DuplicateGroup for groups with 2+ features
 * 5. Return InternalDuplicateIssue for jobs with any duplicate groups
 */
export function detectInternalDuplicates(jobs: Job[]): InternalDuplicateIssue[] {
	const issues: InternalDuplicateIssue[] = [];

	for (const job of jobs) {
		const duplicateGroups: DuplicateGroup[] = [];
		const filePaths: string[] = [];

		// Process points - use proximity-based grouping (30ft tolerance)
		const pointFeatures = getPointFeatures(job);
		const pointsWithIndices = pointFeatures.map((feature, index) => ({ feature, index }));
		const pointGroups = groupPointsByProximity(pointsWithIndices);
		duplicateGroups.push(...pointGroups);

		// Process zones - extract with indices
		const zoneFeatures = getZoneFeatures(job);
		const zonesWithIndices = zoneFeatures.map((feature, index) => ({ feature, index }));
		const zoneGroups = groupFeaturesByCoordinate(zonesWithIndices, "zone");
		// Only include groups with 2+ features (actual duplicates)
		duplicateGroups.push(...zoneGroups.filter((g) => g.features.length > 1));

		// Collect file paths from duplicate groups
		for (const group of duplicateGroups) {
			for (const feature of group.features) {
				if (feature.properties?.FilePath) {
					filePaths.push(feature.properties.FilePath);
				}
			}
		}

		// Only create issue if there are duplicate groups
		if (duplicateGroups.length > 0) {
			issues.push({
				id: issueId("internal-dup", job.id),
				job,
				duplicateGroups,
				filePaths: [...new Set(filePaths)],
			});
		}
	}

	// Sort by % duplicate ascending, then by total feature count ascending
	issues.sort((a, b) => {
		const totalA = getPointFeatures(a.job).length + getZoneFeatures(a.job).length;
		const totalB = getPointFeatures(b.job).length + getZoneFeatures(b.job).length;
		const dupeA = a.duplicateGroups.reduce((sum, g) => sum + g.features.length, 0);
		const dupeB = b.duplicateGroups.reduce((sum, g) => sum + g.features.length, 0);
		const pctA = totalA > 0 ? dupeA / totalA : 0;
		const pctB = totalB > 0 ? dupeB / totalB : 0;

		const pctDiff = pctA - pctB;
		if (pctDiff !== 0) return pctDiff;
		return totalA - totalB;
	});

	return issues;
}

/**
 * Finds jobs with boundaries within a given radius (in miles) of a target job's features.
 *
 * Performance: Uses cached centroids to avoid recomputing for the same boundaries.
 * Optionally accepts a pre-computed centroid cache for batch operations.
 */
export function findNearbyJobs(
	targetJob: Job,
	allJobs: Job[],
	radiusMiles: number,
	centroidCache?: Map<string, Feature<Point>>,
): NearbyJobInfo[] {
	const results: NearbyJobInfo[] = [];

	// Get the target job's centroid
	const targetCentroid = getFeaturesCentroid(targetJob);
	if (!targetCentroid) {
		return [];
	}

	// Check each job (excluding the target job)
	for (const job of allJobs) {
		// Skip the target job itself
		if (job.id === targetJob.id) {
			continue;
		}

		try {
			// Get boundary centroid - use cache if provided, otherwise use per-job cache
			let boundaryCentroid: Feature<Point> | null = null;

			if (centroidCache && centroidCache.has(job.id)) {
				boundaryCentroid = centroidCache.get(job.id) ?? null;
			} else {
				boundaryCentroid = getCachedBoundaryCentroid(job);
			}

			if (!boundaryCentroid) {
				continue;
			}

			// Calculate distance in miles
			const distance = turf.distance(targetCentroid, boundaryCentroid, { units: "miles" });

			if (distance <= radiusMiles) {
				results.push({
					job,
					distanceMiles: distance,
				});
			}
		} catch {
			// Skip jobs with invalid geometries
		}
	}

	// Sort by distance ascending
	results.sort((a, b) => a.distanceMiles - b.distanceMiles);

	return results;
}

/** Minimum overlap area (in square meters) for zone overlaps to report as an issue */
const MIN_ZONE_OVERLAP_AREA_SQ_METERS = 10;

/**
 * Detects overlapping zones within individual jobs.
 * Returns issues for jobs that have 2+ zones that geospatially overlap.
 *
 * Algorithm:
 * 1. For each job, get zone features
 * 2. Pairwise comparison with bbox pre-filter
 * 3. turf.intersect() for actual geometry intersection
 * 4. Filter: only Polygon/MultiPolygon intersections (skip LineString/Point = edge/corner touches)
 * 5. Filter: skip if intersection area <= MIN_ZONE_OVERLAP_AREA_SQ_METERS
 * 6. Union-find for transitive grouping of overlapping zones
 * 7. Return OverlappingZonesIssue per affected job
 */
export function detectOverlappingZones(jobs: Job[]): OverlappingZonesIssue[] {
	const issues: OverlappingZonesIssue[] = [];

	for (const job of jobs) {
		const zones = getZoneFeatures(job);
		if (zones.length < 2) continue;

		// Compute bboxes
		const bboxes = zones.map((z) => getBbox(z));

		// Union-Find
		const n = zones.length;
		const parent = new Array<number>(n);
		const rank = new Array<number>(n);
		for (let i = 0; i < n; i++) {
			parent[i] = i;
			rank[i] = 0;
		}

		function find(x: number): number {
			while (parent[x] !== x) {
				parent[x] = parent[parent[x]]; // path compression
				x = parent[x];
			}
			return x;
		}

		function union(a: number, b: number): void {
			const ra = find(a);
			const rb = find(b);
			if (ra === rb) return;
			if (rank[ra] < rank[rb]) {
				parent[ra] = rb;
			} else if (rank[ra] > rank[rb]) {
				parent[rb] = ra;
			} else {
				parent[rb] = ra;
				rank[ra]++;
			}
		}

		// Track max pairwise intersection area per group root
		const pairAreas = new Map<string, number>(); // "i-j" -> area
		let hasOverlap = false;

		// O(n²) pairwise comparison with bbox pre-filter
		for (let i = 0; i < n; i++) {
			for (let j = i + 1; j < n; j++) {
				if (!bboxesIntersect(bboxes[i], bboxes[j])) continue;

				try {
					const intersection = turf.intersect(turf.featureCollection([zones[i], zones[j]]));
					if (!intersection) continue;

					// Only count Polygon/MultiPolygon intersections (not LineString/Point = edge/corner touches)
					const geomType = intersection.geometry.type;
					if (geomType !== "Polygon" && geomType !== "MultiPolygon") continue;

					const area = turf.area(intersection);
					if (area <= MIN_ZONE_OVERLAP_AREA_SQ_METERS) continue;

					// These two zones overlap
					union(i, j);
					hasOverlap = true;
					pairAreas.set(`${i}-${j}`, area);
				} catch {
					// Skip invalid geometries
				}
			}
		}

		if (!hasOverlap) continue;

		// Collect groups by root
		const groupMap = new Map<number, number[]>();
		for (let i = 0; i < n; i++) {
			const root = find(i);
			const existing = groupMap.get(root);
			if (existing) {
				existing.push(i);
			} else {
				groupMap.set(root, [i]);
			}
		}

		// Build OverlapGroup[] (only groups with 2+ zones)
		const overlapGroups: OverlapGroup[] = [];
		for (const indices of groupMap.values()) {
			if (indices.length < 2) continue;

			// Sort indices for deterministic IDs
			indices.sort((a, b) => a - b);

			// Find max pairwise intersection area within this group
			let maxArea = 0;
			for (let x = 0; x < indices.length; x++) {
				for (let y = x + 1; y < indices.length; y++) {
					const key = `${indices[x]}-${indices[y]}`;
					const area = pairAreas.get(key) ?? 0;
					if (area > maxArea) maxArea = area;
				}
			}

			const groupId = `ozg-${indices.join("-")}`;

			overlapGroups.push({
				id: groupId,
				features: indices.map((i) => zones[i]),
				featureIndices: indices,
				intersectionArea: maxArea,
			});
		}

		if (overlapGroups.length === 0) continue;

		// Collect file paths
		const filePaths = new Set<string>();
		for (const group of overlapGroups) {
			for (const feature of group.features) {
				if (feature.properties?.FilePath) {
					filePaths.add(feature.properties.FilePath);
				}
			}
		}

		issues.push({
			id: issueId("overlap-zones", job.id),
			job,
			overlapGroups,
			filePaths: [...filePaths],
		});
	}

	return issues;
}

/**
 * Detects jobs that have features (points/zones) but no boundary.
 *
 * Performance: Pre-computes centroids for all jobs with boundaries once,
 * then reuses them for all findNearbyJobs calls.
 */
export function detectUnmatchedJobs(jobs: Job[]): UnmatchedIssue[] {
	const issues: UnmatchedIssue[] = [];

	// Pre-compute centroids for all jobs with boundaries
	// This avoids recomputing centroids in each findNearbyJobs call
	const centroidCache = new Map<string, Feature<Point>>();
	for (const job of jobs) {
		const centroid = getCachedBoundaryCentroid(job);
		if (centroid) {
			centroidCache.set(job.id, centroid);
		}
	}

	for (const job of jobs) {
		// Check if job has features but no boundary
		if (hasFeatures(job) && !hasBoundary(job)) {
			// Get the unmatched features
			const points = getPointFeatures(job);
			const zones = getZoneFeatures(job);
			const allFeatures = [...points, ...zones];

			// Create a FeatureCollection of unmatched features
			const unmatchedFeatures: FeatureCollection = {
				type: "FeatureCollection",
				features: allFeatures,
			};

			// Get file path from features
			const filePath = getFilePath(allFeatures);

			// Find nearby jobs with boundaries (within 2 miles)
			// Pass the pre-computed centroid cache for performance
			const nearbyJobs = findNearbyJobs(job, jobs, 2, centroidCache);

			issues.push({
				id: issueId("unmatched", job.id),
				job,
				unmatchedFeatures,
				filePath,
				nearbyJobs,
			});
		}
	}

	return issues;
}

/**
 * Detects jobs that have a boundary but no features (points/zones).
 * This is the inverse of detectUnmatchedJobs.
 */
export function detectMissingFeatures(jobs: Job[]): MissingFeaturesIssue[] {
	const issues: MissingFeaturesIssue[] = [];

	for (const job of jobs) {
		if (hasBoundary(job) && !hasFeatures(job)) {
			issues.push({
				id: issueId("missing-features", job.id),
				job,
			});
		}
	}

	return issues;
}
